<template>
    <button
        type="button"
        class="flex items-center gap-2 px-4 py-2"
        @click="handleVerifyKiosk"
    >
        <check-circle-icon
            v-if="kioskName === ''"
            class="h-6 w-6"
        />
        <shield-check-icon
            v-else
            class="h-6 w-6"
        />
        {{ kioskName }}
    </button>

    <prime-dialog
        v-model:visible="showPromptToVerify"
        modal
        :draggable="false"
        :closable="!isPending"
        :close-on-escape="!isPending"
        :header="t('layout.verifyKiosk')"
        class="!w-[25rem]"
        :pt="{
            rejectButton: {
                outlined: true
            }
        }"
        :pt-options="{
            mergeSections: true,
            mergeProps: true
        }"
    >
        <div class="flex w-full flex-col items-center gap-3">
            <i class="pi pi-check-circle text-4xl text-primary-500"></i>
            <p>{{ t('layout.enterKioskCodePrompt') }}</p>
            <input-text
                v-model="verifyKioskCode"
                class="js-VerifyKioskCode w-full !text-[16px]"
                :placeholder="t('layout.enterCode')"
                :disabled="isPending"
                @keydown.enter="handleEnterVerifyCode"
            />
            <rp-hcaptcha
                v-if="loginModalOrganization.loginHcaptchaEnabled"
                ref="hcaptchaRef"
                :site-key="loginModalOrganization.loginHcaptchaSiteKey"
                @challenge-opened="hcaptchaChallengeOpened = true"
            />
            <div
                v-if="isPending"
                role="status"
                class="flex items-center gap-2 text-sm text-gray-600"
            >
                <i
                    class="pi pi-spin pi-spinner"
                    aria-hidden="true"
                />
                <span>
                    {{
                        isCollectingHcaptcha
                            ? t('completingSecurityCheck')
                            : t('layout.kioskVerificationPending')
                    }}
                </span>
            </div>
            <span
                v-if="verifyKioskError !== ''"
                class="text-red-600"
            >
                {{ verifyKioskError }}
            </span>
        </div>

        <template #footer>
            <prime-button
                :label="t('cancel')"
                outlined
                autofocus
                class="mr-auto"
                :disabled="isPending"
                @click="showPromptToVerify = false"
            />

            <prime-button
                :label="t('codeVerify')"
                autofocus
                :disabled="isPending || verifyKioskCode.length !== 6"
                @click="handleEnterVerifyCode"
            />
        </template>
    </prime-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PrimeButton from 'primevue/button'
import PrimeDialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { ShieldCheckIcon } from '@heroicons/vue/24/solid'
import { CheckCircleIcon } from '@heroicons/vue/24/outline'
import RpHcaptcha from '~/components/Verification/RpHcaptcha.vue'
import { useVerifiedKiosk } from '~/composables/use-verified-kiosk'
import { useLoginModalOrganization } from '~/composables/use-login-modal-organization'

const { t } = useI18n({ useScope: 'global' })

const showPromptToVerify = ref(false)
const verifyKioskCode = ref('')
const verifyKioskError = ref('')
const loginModalOrganization = useLoginModalOrganization()
const hcaptchaRef = ref<InstanceType<typeof RpHcaptcha>>()
const hcaptchaChallengeOpened = ref(false)
const isCollectingHcaptcha = ref(false)
const isPending = ref(false)
const { kioskName, verifyCode } = useVerifiedKiosk({
    onSuccess: () => {
        showPromptToVerify.value = false
    },
    onError: message => {
        verifyKioskError.value = message
        setTimeout(() => {
            document.querySelector('.js-VerifyKioskCode')?.select?.()
        })
    },
    onSettled: resetHcaptcha
})
function handleVerifyKiosk() {
    if (kioskName.value !== '') {
        return
    }

    verifyKioskCode.value = ''
    verifyKioskError.value = ''
    showPromptToVerify.value = true
    setTimeout(() => {
        document.querySelector('.js-VerifyKioskCode')?.focus?.()
    }, 50)
}

watch(verifyKioskCode, () => {
    verifyKioskError.value = ''
})

async function handleEnterVerifyCode() {
    if (isPending.value || verifyKioskCode.value.length !== 6) {
        return
    }

    isPending.value = true
    verifyKioskError.value = ''
    hcaptchaChallengeOpened.value = false

    let hcaptchaToken: string | undefined
    try {
        if (loginModalOrganization.value.loginHcaptchaEnabled) {
            isCollectingHcaptcha.value = true
            hcaptchaToken = await hcaptchaRef.value?.execute()
            if (hcaptchaToken === undefined || hcaptchaToken === '') {
                verifyKioskError.value = t('captchaRequired')
                resetHcaptcha()
                return
            }
        }
    } catch (error) {
        if (
            error instanceof Error &&
            [
                'hcaptcha-closed',
                'hcaptcha-expired',
                'hcaptcha-challenge-expired'
            ].includes(error.message)
        ) {
            verifyKioskError.value = t('captchaRequired')
        } else {
            verifyKioskError.value = t('signInFlow.refreshAndTryAgain')
        }
        resetHcaptcha()
        return
    } finally {
        isCollectingHcaptcha.value = false
    }

    verifyCode({
        code: verifyKioskCode.value,
        hcaptchaToken,
        hcaptchaChallengeOpened: hcaptchaChallengeOpened.value
    })
}

function resetHcaptcha() {
    hcaptchaRef.value?.reset()
    hcaptchaChallengeOpened.value = false
    isPending.value = false
}
</script>
